import { useEffect } from 'react'
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown'
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp'
import { Button, Paper } from '@mui/material'
import { useAuth } from 'context'
import { SearchParams } from 'location.types'
import { useSearchParams } from 'react-router'
import { useLocalStorage } from 'utils/localStorage'
import { MaestroBox } from './MaestroBox'

/*
 * Maestro for web is still on beta. This component adds some functionality in order to help
 * Maestro interacting with the page.
 */
export const MaestroBar = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [storedMaestroFlag, setStoredMaestroFlag] = useLocalStorage(SearchParams.MaestroTest)
  const maestroQueryValue = searchParams.get(SearchParams.MaestroTest)
  const { signOut } = useAuth()

  useEffect(() => {
    if (maestroQueryValue) {
      setStoredMaestroFlag(maestroQueryValue)
    }
  }, [maestroQueryValue, setStoredMaestroFlag])

  const isMaestroTest = (maestroQueryValue ?? storedMaestroFlag) === 'true'

  if (!isMaestroTest) return null

  const findAndScroll = (target: 'top' | 'bottom') => {
    const allElements = document.querySelectorAll('*')

    allElements.forEach((element) => {
      // Check if the element is actually scrollable
      if (element.scrollHeight > element.clientHeight) {
        const style = window.getComputedStyle(element)
        const isScrollable = style.overflowY === 'auto' || style.overflowY === 'scroll'
        const isHtml = element === document.documentElement

        if (isScrollable || isHtml) {
          element.scrollTo({
            // If target is 'top', go to 0. Otherwise, go to max height.
            top: target === 'top' ? 0 : element.scrollHeight,
            behavior: 'auto' // Instant scroll is best for Maestro
          })
        }
      }
    })
  }

  const scrollToBottom = () => findAndScroll('bottom')

  const scrollToTop = () => findAndScroll('top')

  const resetState = async () => {
    try {
      await signOut()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    } finally {
      window.localStorage.clear()
      window.sessionStorage.clear()
      window.location.reload()
    }
  }

  const hideBar = () => {
    const params = new URLSearchParams(searchParams)
    params.delete(SearchParams.MaestroTest)
    setSearchParams(params, { replace: true })
    setStoredMaestroFlag('false')
  }

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'fixed',
        top: 10,
        right: 10,
        padding: '8px',
        display: 'flex',
        gap: 1,
        zIndex: 9999
      }}
    >
      <MaestroBox id="hide-bar">
        <Button variant="outlined" size="small" color="secondary" onClick={hideBar}>
          Hide Bar
        </Button>
      </MaestroBox>
      <MaestroBox id="reset-state">
        <Button variant="outlined" size="small" color="secondary" onClick={resetState}>
          Reset State
        </Button>
      </MaestroBox>
      <MaestroBox id="scroll-to-top">
        <Button variant="contained" size="small" onClick={scrollToTop}>
          <KeyboardDoubleArrowUpIcon />
          Scroll to Top
        </Button>
      </MaestroBox>
      <MaestroBox id="scroll-to-bottom">
        <Button variant="contained" size="small" onClick={scrollToBottom}>
          <KeyboardDoubleArrowDownIcon />
          Scroll to Bottom
        </Button>
      </MaestroBox>
    </Paper>
  )
}
